import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref, withDefaults } from 'vue';
import { WindowCanvasConfig } from '@/interfaces/components/combinations/WindowCanvasConfig';
import { DynamicCanvasSizes } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import { useGuideImages } from '@/modules/ConfiguratorCombinations/composables/useGuideImages';
import { useWindowDescriptions } from '@/modules/ConfiguratorCombinations/composables/useWindowDescriptions';

interface CircleLetter {
    letter: string;
    visible: boolean;
}

interface Props {
    windowConfig: WindowCanvasConfig;
    index?: number;
    guideType: string;
    secondGuideType: string;
    isFirstWindow?: boolean;
    isLastWindow?: boolean;
    combinationType?: string;
    isLastStep: boolean;
    widthDifference: number;
    totalProductsSelected: number;
    currentStep: number;
    currentProductFormId: number;
    canvasTemplateConfiguration: DynamicCanvasSizes;
    circleLetters?: CircleLetter[];
    useSeparateBoxes: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CombinationsConfiguratorCanvasHero',
  props: {
    windowConfig: null,
    index: null,
    guideType: null,
    secondGuideType: null,
    isFirstWindow: { type: Boolean, default: false },
    isLastWindow: { type: Boolean, default: false },
    combinationType: { default: '' },
    isLastStep: { type: Boolean },
    widthDifference: null,
    totalProductsSelected: null,
    currentStep: null,
    currentProductFormId: null,
    canvasTemplateConfiguration: null,
    circleLetters: { default: () => [] },
    useSeparateBoxes: { type: Boolean }
  },
  setup(__props: any, { expose }) {

const props = __props as { windowConfig: WindowCanvasConfig, index?: number, guideType: string, secondGuideType: string, isFirstWindow: boolean, isLastWindow: boolean, combinationType: string, isLastStep: boolean, widthDifference: number, totalProductsSelected: number, currentStep: number, currentProductFormId: number, canvasTemplateConfiguration: DynamicCanvasSizes, circleLetters: CircleLetter[], useSeparateBoxes: boolean };



const { proxy } = getCurrentInstance()!;

// ----------------------------------------------------------------------------
// Layout constants (HERO-specific proportions)
// ----------------------------------------------------------------------------
const slatHeight = 11; // visible height of each lamella
const slatGap = 6; // dark gap between lamellas (background shows through)
const railWidth = 20;
const headerHeight = 50;
const bottomLineHeight = 10;
const shutterAreaRatio = 0.65; // slats occupy this fraction of the frame body height
const grayColor = '#cccccc';

// ----------------------------------------------------------------------------
// Template refs
// ----------------------------------------------------------------------------
const windowGroup = ref<any>(null);
const dividerLine = ref<any>(null);

// ----------------------------------------------------------------------------
// Localized labels
// ----------------------------------------------------------------------------
const topViewDescription: any = proxy.$t('Top-down View');
const windowDescription: any = proxy.$t('View From the Inside Out');

// ----------------------------------------------------------------------------
// Base dimensions (shared across nearly every config)
// ----------------------------------------------------------------------------
const transformedDimensions = computed<{ height: number; width: number }>(() => ({
    height: parseFloat(props.windowConfig.height as any) * 10,
    width: parseFloat(props.windowConfig.width as any) * 10,
}));

const groupScaleX = computed(() => 0.42);
const groupScaleY = computed(() => 0.42);

const frameWidth = computed(() => transformedDimensions.value.width * groupScaleX.value);
const frameHeight = computed(() => transformedDimensions.value.height * groupScaleY.value);

const windowPosition = computed(() =>
    props.windowConfig?.position ? parseInt(props.windowConfig.position.toString(), 10) : null,
);

const groupX = computed(() =>
    props.windowConfig.positionX !== null ? props.windowConfig.positionX : props.windowConfig.initialPositionX,
);

const groupY = computed(() =>
    props.windowConfig.positionY !== null ? props.windowConfig.positionY : props.windowConfig.initialPositionY,
);

// ----------------------------------------------------------------------------
// Description / guide composables (shared with the standard canvas)
// ----------------------------------------------------------------------------
const { descriptionYOffset, descriptionTextConfig, textBackgroundConfig } = useWindowDescriptions({
    topViewDescription: () => topViewDescription,
    windowDescription: () => windowDescription,
    canvasTemplateConfiguration: () => props.canvasTemplateConfiguration,
});

const {
    shouldShowPartitionGuide,
    showLeftGuideImage,
    showRightGuideImage,
    showMiddleGuideImage,
    leftGuideImageConfig,
    rightGuideImageConfig,
    middleGuideImageConfig,
} = useGuideImages({
    guideType: () => props.guideType,
    secondGuideType: () => props.secondGuideType,
    isFirstWindow: () => props.isFirstWindow,
    isLastWindow: () => props.isLastWindow,
    isLastStep: () => props.isLastStep,
    windowPosition: () => windowPosition.value,
    frameWidth: () => frameWidth.value,
    canvasTemplateConfiguration: () => props.canvasTemplateConfiguration,
});

// ----------------------------------------------------------------------------
// Top label bar
// ----------------------------------------------------------------------------
const windowGroupYOffset = computed<number>(() => props.canvasTemplateConfiguration.windowGroupYOffset ?? 320);

const rectangleConfig = computed(() => ({
    x: 0,
    y: 0,
    width: frameWidth.value,
    height: 70,
    fill: grayColor,
    stroke: '#333333',
    strokeWidth: 1,
    cornerRadius: 4,
    perfectDrawEnabled: true,
    shadowForStrokeEnabled: false,
    strokeScaleEnabled: false,
}));

const positionTextConfig = computed(() => ({
    x: frameWidth.value / 2,
    y: 30,
    text: props.windowConfig.position?.toString() || '',
    fontSize: 24,
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontStyle: 'bold',
    fontVariant: 'normal',
    fill: '#333333',
    align: 'center',
    verticalAlign: 'middle',
    perfectDrawEnabled: false,
    shadowForStrokeEnabled: false,
    hitStrokeWidth: 0,
    listening: false,
    transformsEnabled: 'position',
    visible: true,
}));

const isUsingExtraWidth = computed(() => props.widthDifference > 0);

const extraWidth = computed(() => {
    if (isUsingExtraWidth.value) {
        return Math.floor(props.widthDifference / 2);
    }

    return 0;
});

const isStepThreeOrHigher = computed(() => props.currentStep >= 3);

const showExtraWindowWidth = computed(() => {
    return isUsingExtraWidth.value && isStepThreeOrHigher.value && props.totalProductsSelected >= 2;
});

const dashedExtraWidthConfig = computed(() => {
    const extraWidthValue = extraWidth.value * 10 * groupScaleX.value;

    if (props.isFirstWindow) {
        return {
            x: -extraWidthValue,
            y: 0,
            width: extraWidthValue,
            height: 70,
            stroke: '#1a53ff',
            strokeWidth: 1,
            dash: [3, 3],
            fill: 'rgba(230, 240, 255, 1)',
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
        };
    } else if (props.isLastWindow) {
        return {
            x: frameWidth.value,
            y: 0,
            width: extraWidthValue,
            height: 70,
            stroke: '#1a53ff',
            strokeWidth: 1,
            dash: [3, 3],
            fill: 'rgba(230, 240, 255, 1)',
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
        };
    }

    return {
        visible: false,
    };
});

const circlesConfig = computed(() => {
    const circleRadius = 22;
    const spacing = 40;
    const startX = 30;

    const defaultCircles = [
        { letter: 'L', visible: true },
        { letter: 'D', visible: false },
    ];

    const circles = props.circleLetters && props.circleLetters.length > 0 ? props.circleLetters : defaultCircles;

    return circles.map((item, idx) => {
        let x: number;
        switch (item.letter) {
            case 'L':
                x = startX;
                break;
            case 'D':
                x = frameWidth.value - startX;
                break;
            default:
                x = startX + idx * spacing;
        }

        return {
            visible: item.visible,
            config: {
                x,
                y: 33,
                radius: circleRadius,
                fill: '#1a53ff',
                stroke: '#0033cc',
                strokeWidth: 1,
                perfectDrawEnabled: true,
                shadowForStrokeEnabled: false,
                hitStrokeWidth: 0,
                strokeScaleEnabled: false,
            },
            textConfig: {
                x: x - 5,
                y: 25,
                text: item.letter,
                fontSize: 20,
                fontFamily: 'Arial',
                fill: 'white',
                align: 'center',
                perfectDrawEnabled: true,
                shadowForStrokeEnabled: false,
                lineJoin: 'round',
                fontStyle: 'bold',
            },
        };
    });
});

// ----------------------------------------------------------------------------
// Window body — header overhang, slats, inset opening, rails
// ----------------------------------------------------------------------------
const railColors = computed(() => {
    const blueFill = '#1a53ff';
    const blueOutline = '#0033cc';
    const isStepFour = props.currentStep === 4;

    return {
        leftFill: showLeftGuideImage.value && isStepFour ? blueFill : grayColor,
        leftStroke: showLeftGuideImage.value && isStepFour ? blueOutline : grayColor,
        rightFill: showRightGuideImage.value && isStepFour ? blueFill : grayColor,
        rightStroke: showRightGuideImage.value && isStepFour ? blueOutline : grayColor,
    };
});

const partitionGuideColorConfig = computed(() => {
    const yellowColor = '#FFD700';
    const outlineColor = '#FFC107';

    return {
        lineColor: shouldShowPartitionGuide.value ? yellowColor : grayColor,
        strokeColor: shouldShowPartitionGuide.value ? outlineColor : grayColor,
    };
});

const headerOverhangConfig = computed(() => ({
    x: 0,
    y: 0,
    width: frameWidth.value,
    height: headerHeight,
    fill: grayColor,
    stroke: '#1d1d1d',
    strokeWidth: 1.5,
}));

// Body of the frame (between header and bottom line) split into:
//   - shutter area (top portion, fixed ratio): slats + dark gaps
//   - track area (bottom portion): a few thin horizontal lines + the inset window
const bodyTop = computed(() => headerHeight);
const bodyBottom = computed(() => frameHeight.value - bottomLineHeight);
const bodyHeight = computed(() => bodyBottom.value - bodyTop.value);

const shutterAreaHeight = computed(() => bodyHeight.value * shutterAreaRatio);
const shutterAreaTop = computed(() => bodyTop.value);
const shutterAreaBottom = computed(() => bodyTop.value + shutterAreaHeight.value);

const trackAreaTop = computed(() => shutterAreaBottom.value);
const trackAreaBottom = computed(() => bodyBottom.value);
const trackAreaHeight = computed(() => trackAreaBottom.value - trackAreaTop.value);

const slats = computed(() => {
    const totalStep = slatHeight + slatGap;
    const totalSlats = Math.floor(shutterAreaHeight.value / totalStep);
    const result = [];

    for (let i = 0; i < totalSlats; i++) {
        result.push({
            y: shutterAreaTop.value + i * totalStep,
            height: slatHeight,
        });
    }

    return result;
});

// Light background that shows through between slats, giving the visible gaps.
const slatsBackgroundConfig = computed(() => ({
    x: railWidth,
    y: shutterAreaTop.value,
    width: frameWidth.value - railWidth * 2,
    height: shutterAreaHeight.value,
    fill: '#ffffff',
    stroke: '#bdbdbd',
    strokeWidth: 0.5,
}));

function slatConfig(slat: { y: number; height: number }) {
    return {
        x: railWidth,
        y: slat.y,
        width: frameWidth.value - railWidth * 2,
        height: slat.height,
        fill: '#b8b8b8',
    };
}

// Light area below the shutters (empty panel with subtle border).
const trackAreaBackgroundConfig = computed(() => ({
    x: railWidth,
    y: trackAreaTop.value,
    width: frameWidth.value - railWidth * 2,
    height: trackAreaHeight.value,
    fill: '#ffffff',
    stroke: '#bdbdbd',
    strokeWidth: 0.5,
}));

const innerLineThickness = 3;
const innerFill = '#3a3a3a';
const outerRectInset = 25;
const nestedRectInset = 20;
const outerRectBottomOffset = 10;
const nestedRectBottomOffset = 15;

const outerRectLeftX = computed(() => railWidth + outerRectInset);
const outerRectRightX = computed(() => frameWidth.value - railWidth - outerRectInset);
const outerRectTop = computed(() => headerHeight);
const outerRectBottom = computed(() => frameHeight.value - bottomLineHeight - outerRectBottomOffset);
const outerRectHeight = computed(() => outerRectBottom.value - outerRectTop.value);

const innerRectLeftX = computed(() => outerRectLeftX.value + nestedRectInset);
const innerRectRightX = computed(() => outerRectRightX.value - nestedRectInset);
const innerRectTop = computed(() => outerRectTop.value);
const innerRectBottom = computed(() => outerRectBottom.value - nestedRectBottomOffset);
const innerRectHeight = computed(() => innerRectBottom.value - innerRectTop.value);

const outerRectLeftLineConfig = computed(() => ({
    x: outerRectLeftX.value,
    y: outerRectTop.value,
    width: innerLineThickness,
    height: outerRectHeight.value,
    fill: innerFill,
}));

const outerRectRightLineConfig = computed(() => ({
    x: outerRectRightX.value - innerLineThickness,
    y: outerRectTop.value,
    width: innerLineThickness,
    height: outerRectHeight.value,
    fill: innerFill,
}));

const outerRectBottomLineConfig = computed(() => ({
    x: outerRectLeftX.value,
    y: outerRectBottom.value - innerLineThickness,
    width: outerRectRightX.value - outerRectLeftX.value,
    height: innerLineThickness,
    fill: innerFill,
}));

const innerRectLeftLineConfig = computed(() => ({
    x: innerRectLeftX.value,
    y: innerRectTop.value,
    width: innerLineThickness,
    height: innerRectHeight.value,
    fill: innerFill,
}));

const innerRectRightLineConfig = computed(() => ({
    x: innerRectRightX.value - innerLineThickness,
    y: innerRectTop.value,
    width: innerLineThickness,
    height: innerRectHeight.value,
    fill: innerFill,
}));

const innerRectBottomLineConfig = computed(() => ({
    x: innerRectLeftX.value,
    y: innerRectBottom.value - innerLineThickness,
    width: innerRectRightX.value - innerRectLeftX.value,
    height: innerLineThickness,
    fill: innerFill,
}));

const leftRailConfig = computed(() => ({
    x: 0,
    y: 0,
    width: railWidth,
    height: frameHeight.value,
    fill: railColors.value.leftFill,
    stroke: railColors.value.leftStroke,
    strokeWidth: 1,
}));

const rightRailConfig = computed(() => ({
    x: frameWidth.value - railWidth,
    y: 0,
    width: railWidth,
    height: frameHeight.value,
    fill: railColors.value.rightFill,
    stroke: railColors.value.rightStroke,
    strokeWidth: 1,
}));

const bottomLineConfig = computed(() => ({
    x: 0,
    y: frameHeight.value - bottomLineHeight,
    width: frameWidth.value,
    height: bottomLineHeight,
    fill: '#ffffff',
    stroke: '#1d1d1d',
    strokeWidth: 1,
}));

const verticalLineConfig = computed(() => {
    const lineWidth = 65;
    const xOffset = 30;
    const yOffset = 2;
    const { lineColor, strokeColor } = partitionGuideColorConfig.value;

    return {
        x: frameWidth.value - xOffset,
        y: headerHeight + yOffset,
        width: lineWidth,
        height: frameHeight.value - headerHeight,
        fill: lineColor,
        stroke: strokeColor,
        strokeWidth: 1,
        opacity: 1,
    };
});

// Public method mirrored from the standard canvas so the parent can re-stack.
function moveWindowToTop(): boolean {
    if (windowGroup.value) {
        const node = (windowGroup.value as any)._konvaNode;
        if (node) {
            node.moveToTop();
            return true;
        }
    }

    return false;
}

expose({ moveWindowToTop });

return { __sfc: true,props, proxy, slatHeight, slatGap, railWidth, headerHeight, bottomLineHeight, shutterAreaRatio, grayColor, windowGroup, dividerLine, topViewDescription, windowDescription, transformedDimensions, groupScaleX, groupScaleY, frameWidth, frameHeight, windowPosition, groupX, groupY, descriptionYOffset, descriptionTextConfig, textBackgroundConfig, shouldShowPartitionGuide, showLeftGuideImage, showRightGuideImage, showMiddleGuideImage, leftGuideImageConfig, rightGuideImageConfig, middleGuideImageConfig, windowGroupYOffset, rectangleConfig, positionTextConfig, isUsingExtraWidth, extraWidth, isStepThreeOrHigher, showExtraWindowWidth, dashedExtraWidthConfig, circlesConfig, railColors, partitionGuideColorConfig, headerOverhangConfig, bodyTop, bodyBottom, bodyHeight, shutterAreaHeight, shutterAreaTop, shutterAreaBottom, trackAreaTop, trackAreaBottom, trackAreaHeight, slats, slatsBackgroundConfig, slatConfig, trackAreaBackgroundConfig, innerLineThickness, innerFill, outerRectInset, nestedRectInset, outerRectBottomOffset, nestedRectBottomOffset, outerRectLeftX, outerRectRightX, outerRectTop, outerRectBottom, outerRectHeight, innerRectLeftX, innerRectRightX, innerRectTop, innerRectBottom, innerRectHeight, outerRectLeftLineConfig, outerRectRightLineConfig, outerRectBottomLineConfig, innerRectLeftLineConfig, innerRectRightLineConfig, innerRectBottomLineConfig, leftRailConfig, rightRailConfig, bottomLineConfig, verticalLineConfig, moveWindowToTop }
}

})